import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { useAppServices } from "../../../../hook/services";
import { useKnowledgebaseCustomizationInfo } from "../../../../context/knowledgebaseCustomization";

const ArticleDetail = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const AppService = useAppServices();
  const [KnowledgebaseCustomization] = useKnowledgebaseCustomizationInfo();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isHelpful, setIsHelpful] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const getArticle = async () => {
    setLoading(true);
    try {
      const { response } = await AppService.knowledgebase_article.filter({
        query: `_id=${articleId}`,
      });
      if (response?.data) {
        setArticle(response.data);
      } else {
        setArticle(null);
      }
    } catch (error) {
      console.error(error);
      setArticle(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getArticle();
  }, [articleId]);

  if (loading) {
    return (
      <div className="kb-page d-flex flex-column align-items-center justify-content-center py-5 text-secondary" style={{ minHeight: "60vh" }}>
        <div className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true" />
        <span style={{ fontSize: "0.875rem" }}>Loading article…</span>
        <KbStyles />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="kb-page">
        <div className="container py-5">
          <div className="kb-empty d-flex flex-column align-items-center text-center mx-auto" style={{ maxWidth: 460 }}>
            <div className="kb-empty-icon d-flex align-items-center justify-content-center mb-3">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
            </div>
            <h5 className="kb-empty-title">Article not found</h5>
            <p className="kb-empty-text mb-3">
              The article you're looking for doesn't exist or may have been moved.
            </p>
            <div className="d-flex gap-2 flex-wrap justify-content-center">
              <button className="kb-btn kb-btn--ghost" onClick={() => navigate(-1)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Go back
              </button>
              <Link to="/help" className="kb-btn kb-btn--primary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Help center
              </Link>
            </div>
          </div>
        </div>
        <KbStyles />
      </div>
    );
  }

  const handleHelpful = (value) => {
    setIsHelpful(value);
    setShowFeedback(true);
    console.log(`Article helpful: ${value}`);
  };

  const headerTextColor = KnowledgebaseCustomization?.textColor || "white";

  return (
    <div className="kb-page">
      {/* Hero */}
      <header
        className="kb-hero"
        style={{ background: KnowledgebaseCustomization?.bgColor || "#6c757d" }}
      >
        <div className="container kb-hero-inner">
          <div className="d-flex justify-content-start mb-3">
            <button
              type="button"
              className="kb-hero-back"
              onClick={() => navigate(-1)}
              style={{ color: headerTextColor }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back</span>
            </button>
          </div>

          <div className="text-center">
            <span className="kb-hero-icon d-inline-flex align-items-center justify-content-center mb-3" style={{ color: headerTextColor }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="8" y1="13" x2="16" y2="13" />
                <line x1="8" y1="17" x2="13" y2="17" />
              </svg>
            </span>
            <h1 className="kb-hero-title" style={{ color: headerTextColor }}>
              {article.name}
            </h1>
            {article.shortDescription && (
              <p className="kb-hero-subtitle" style={{ color: headerTextColor }}>
                {article.shortDescription}
              </p>
            )}
            <span className="kb-hero-badge">
              Updated {new Date(article.updatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container kb-detail-content">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-9">
            <article className="kb-detail-article">
              <div
                className="kb-article-body"
                dangerouslySetInnerHTML={{ __html: article.doc }}
              />
            </article>

            <div className="kb-detail-feedback text-center">
              <h5 className="kb-feedback-title">Was this article helpful?</h5>

              {!showFeedback ? (
                <div className="d-flex gap-2 justify-content-center">
                  <button
                    className="kb-feedback-btn kb-feedback-btn--yes"
                    onClick={() => handleHelpful(true)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                    </svg>
                    Yes
                  </button>
                  <button
                    className="kb-feedback-btn kb-feedback-btn--no"
                    onClick={() => handleHelpful(false)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zM17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
                    </svg>
                    No
                  </button>
                </div>
              ) : (
                <div className={`kb-feedback-result ${isHelpful ? "is-positive" : "is-negative"}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill={isHelpful ? "#16a34a" : "#dc2626"} stroke="none" className="me-1">
                    {isHelpful ? (
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                    ) : (
                      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zM17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
                    )}
                  </svg>
                  Thank you for your feedback!
                </div>
              )}
            </div>

            <div className="text-center mt-4">
              <button
                className="kb-btn kb-btn--ghost"
                onClick={() => navigate(-1)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-1">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back to articles
              </button>
            </div>
          </div>
        </div>
      </div>

      <KbStyles />
    </div>
  );
};

const KbStyles = () => (
  <style>{`
    .kb-page {
      font-family: 'Inter', sans-serif;
      background: #fff;
      min-height: 100vh;
    }

    /* Hero */
    .kb-hero {
      padding: 22px 16px 48px;
    }
    .kb-hero-inner { max-width: 760px; margin: 0 auto; }
    .kb-hero-back {
      display: inline-flex;
      align-items: center;
      padding: 6px 10px;
      border-radius: 8px;
      text-decoration: none;
      background: rgba(255, 255, 255, 0.16);
      border: 1px solid rgba(255, 255, 255, 0.18);
      cursor: pointer;
      transition: background 0.15s ease, transform 0.15s ease;
    }
    .kb-hero-back:hover {
      background: rgba(255, 255, 255, 0.26);
      text-decoration: none;
    }
    .kb-hero-back:hover svg { transform: translateX(-2px); }
    .kb-hero-back svg { transition: transform 0.15s ease; }
    .kb-hero-icon {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.16);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }
    .kb-hero-title {
      font-size: 1.875rem;
      font-weight: 700;
      letter-spacing: -0.012em;
      margin: 0 0 10px;
    }
    .kb-hero-subtitle {
      font-size: 0.9375rem;
      opacity: 0.92;
      margin: 0 0 16px;
    }
    .kb-hero-badge {
      display: inline-flex;
      align-items: center;
      padding: 5px 12px;
      font-size: 0.75rem;
      font-weight: 500;
      color: #1f2937;
      background: rgba(255, 255, 255, 0.92);
      border-radius: 999px;
      letter-spacing: 0.02em;
    }

    /* Detail content */
    .kb-detail-content { padding: 40px 16px 56px; }
    .kb-detail-article {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 14px;
      padding: 32px;
      margin-bottom: 20px;
    }
    .kb-article-body {
      line-height: 1.7;
      color: #1f2937;
      font-size: 0.9375rem;
    }
    .kb-article-body h1,
    .kb-article-body h2,
    .kb-article-body h3,
    .kb-article-body h4 {
      color: #111827;
      letter-spacing: -0.01em;
      margin-top: 1.4em;
      margin-bottom: 0.5em;
    }
    .kb-article-body p { margin-bottom: 1em; }
    .kb-article-body a { color: #4f46e5; text-decoration: none; }
    .kb-article-body a:hover { text-decoration: underline; color: #4338ca; }
    .kb-article-body code {
      background: #f3f4f6;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 0.875em;
      color: #4338ca;
    }
    .kb-article-body pre {
      background: #f9fafb;
      border: 1px solid #ececec;
      border-radius: 8px;
      padding: 14px 16px;
      overflow-x: auto;
    }
    .kb-article-body blockquote {
      border-left: 3px solid #4f46e5;
      padding: 4px 0 4px 16px;
      margin: 1em 0;
      color: #4b5563;
      background: #f5f3ff;
      border-radius: 0 8px 8px 0;
    }

    .kb-detail-feedback {
      background: #fff;
      border: 1px solid #ececec;
      border-radius: 14px;
      padding: 24px;
      margin-bottom: 8px;
    }
    .kb-feedback-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      letter-spacing: -0.005em;
      margin: 0 0 14px;
    }
    .kb-feedback-btn {
      display: inline-flex;
      align-items: center;
      font-size: 0.8125rem;
      font-weight: 600;
      padding: 8px 18px;
      border-radius: 8px;
      border: 1px solid;
      background: #fff;
      cursor: pointer;
      font-family: inherit;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .kb-feedback-btn--yes {
      border-color: #bbf7d0;
      color: #15803d;
    }
    .kb-feedback-btn--yes:hover {
      background: #f0fdf4;
      border-color: #86efac;
      transform: translateY(-1px);
    }
    .kb-feedback-btn--no {
      border-color: #fecaca;
      color: #b91c1c;
    }
    .kb-feedback-btn--no:hover {
      background: #fef2f2;
      border-color: #fca5a5;
      transform: translateY(-1px);
    }
    .kb-feedback-result {
      display: inline-flex;
      align-items: center;
      font-size: 0.875rem;
      font-weight: 600;
      margin-top: 8px;
    }
    .kb-feedback-result.is-positive { color: #15803d; }
    .kb-feedback-result.is-negative { color: #b91c1c; }

    /* Buttons */
    .kb-btn {
      display: inline-flex;
      align-items: center;
      font-size: 0.8125rem;
      font-weight: 600;
      letter-spacing: 0.005em;
      padding: 8px 14px;
      border-radius: 8px;
      border: 1px solid transparent;
      line-height: 1;
      cursor: pointer;
      font-family: inherit;
      text-decoration: none;
      transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.15s ease, color 0.15s ease;
    }
    .kb-btn--ghost {
      background: #fff;
      border-color: #e5e7eb;
      color: #1f2937;
      box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
    }
    .kb-btn--ghost:hover {
      background: #fafafa;
      border-color: #d1d5db;
      color: #1f2937;
      transform: translateY(-1px);
      text-decoration: none;
    }
    .kb-btn--primary {
      background: linear-gradient(180deg, #6366f1 0%, #4f46e5 100%);
      color: #fff;
      border-color: rgba(255, 255, 255, 0.04);
      box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.18) inset,
        0 1px 2px rgba(79, 70, 229, 0.22),
        0 4px 10px rgba(79, 70, 229, 0.2);
    }
    .kb-btn--primary:hover {
      background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
      color: #fff;
      transform: translateY(-1px);
      text-decoration: none;
    }

    /* Empty */
    .kb-empty {
      border: 1px dashed #e5e5e5;
      border-radius: 12px;
      background: #fafafa;
      padding: 40px 24px;
    }
    .kb-empty-icon {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: #fff;
      border: 1px solid #ececec;
      color: #9ca3af;
    }
    .kb-empty-title {
      font-size: 0.9375rem;
      font-weight: 600;
      color: #1f2937;
      margin: 0 0 4px;
    }
    .kb-empty-text {
      font-size: 0.8125rem;
      color: #6b7280;
    }
  `}</style>
);

export default ArticleDetail;
